import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Input } from '@clari/ui'
import { CardPreview } from '@/components/banking/CardPreview'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { bankingApi } from '@/lib/banking-api'

export function CardDetail() {
  const { t } = useTranslation('banking')
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['card', id],
    queryFn: () => bankingApi.getCard(id),
    enabled: Boolean(id),
  })
  const card = q.data?.card

  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editingLimit, setEditingLimit] = useState(false)
  const [limit, setLimit] = useState('')

  async function run(fn: () => Promise<unknown>) {
    setError(null)
    setBusy(true)
    try {
      await fn()
      await qc.invalidateQueries({ queryKey: ['card', id] })
    } catch (e) {
      setError(apiErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto flex max-w-md flex-col gap-4">
      <Link to="/app/cards" className="text-sm text-text-muted hover:text-text">
        {t('transfer.back')}
      </Link>
      {q.isLoading ? <p className="text-text-muted">{t('common.loading')}</p> : null}

      {card ? (
        <>
          <CardPreview card={card} />
          {error ? <ErrorBanner message={error} /> : null}

          <Card>
            <div className="flex flex-wrap gap-2">
              {card.status === 'frozen' ? (
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => run(() => bankingApi.unfreezeCard(card.id))}
                >
                  {t('cards.unfreeze')}
                </Button>
              ) : card.status === 'enabled' ? (
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => run(() => bankingApi.freezeCard(card.id))}
                >
                  {t('cards.freeze')}
                </Button>
              ) : null}

              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  setLimit(String((card.spending_limits?.per_month_cents ?? 0) / 100))
                  setEditingLimit((v) => !v)
                }}
              >
                {t('cards.edit_limits')}
              </Button>

              {card.status !== 'canceled' ? (
                <Button
                  variant="tertiary"
                  disabled={busy}
                  onClick={() => run(() => bankingApi.cancelCard(card.id))}
                >
                  {t('cards.cancel')}
                </Button>
              ) : null}
            </div>

            {editingLimit ? (
              <div className="mt-4 flex items-end gap-2">
                <Input
                  className="flex-1"
                  label={t('cards.spending_limit')}
                  inputMode="decimal"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                />
                <Button
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      const cents = Math.round(Number.parseFloat(limit.replace(',', '.')) * 100)
                      await bankingApi.updateCardLimits(card.id, cents)
                      setEditingLimit(false)
                    })
                  }
                >
                  {t('cards.save')}
                </Button>
              </div>
            ) : null}
          </Card>
        </>
      ) : null}
    </div>
  )
}
